import { useRef, useEffect, useState, useCallback } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { FaCamera, FaRedo, FaDownload, FaShare } from "react-icons/fa";
import { BsArrowsFullscreen, BsArrowsAngleContract } from "react-icons/bs";
import Webcam from "react-webcam";
import apiService from "../../services/api_services.js";
import toast from "react-hot-toast";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import styled from "styled-components";

// Styled components for modern UI
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  z-index: 100;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 767px) {
    align-items: flex-start;
  }
`;

const ModalContainer = styled.div`
  position: relative;
  width: 95%;
  max-width: 720px;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 767px) {
    width: 100%;
    height: 100%;
    border-radius: 0;
    max-width: none;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: white;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
    background: #f8f9fa;
  }
`;

const WebcamContainer = styled.div`
  position: relative;
  height: 70vh;
  width: 100%;
  background: #000;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  
  @media (max-width: 767px) {
    height: calc(100vh - 100px);
  }
`;

const GuideText = styled.div`
  position: absolute;
  top: 20px;
  left: 0;
  right: 0;
  text-align: center;
  color: white;
  font-size: 1.1rem;
  font-weight: 500;
  padding: 10px 20px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  border-radius: 30px;
  margin: 0 auto;
  width: fit-content;
  max-width: 90%;
  z-index: 5;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
`;

const ControlsBar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  gap: 16px;
  background: white;
  
  @media (max-width: 767px) {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(10px);
    padding: 20px;
  }
`;

const CaptureButton = styled.button`
  background: #1F509A;
  color: white;
  border: none;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 122, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(0, 0, 122, 0.4);
  }
  
  svg {
    font-size: 24px;
  }
  
  @media (max-width: 767px) {
    width: 72px;
    height: 72px;
    svg {
      font-size: 28px;
    }
  }
`;

const ActionButton = styled.button`
  background: ${props => props.secondary ? 'white' : '#1F509A'};
  color: ${props => props.secondary ? '#1F509A' : 'white'};
  border: ${props => props.secondary ? '2px solid #1F509A' : 'none'};
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
    background: ${props => props.secondary ? '#f0f4ff' : '#000055'};
  }
  
  svg {
    font-size: 18px;
  }
  
  @media (max-width: 767px) {
    background: ${props => props.secondary ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 122, 0.8)'};
    color: white;
    border: ${props => props.secondary ? '1px solid rgba(255, 255, 255, 0.5)' : 'none'};
    
    &:hover {
      background: ${props => props.secondary ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 122, 0.9)'};
    }
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  z-index: 20;
`;

const Spinner = styled.div`
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 4px solid white;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ProcessedImageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #000;
  overflow: hidden;
  
  @media (max-width: 767px) {
    height: calc(100vh - 100px);
    width: 100%;
  }
`;

const ProcessedImage = styled.img`
  width: ${props => props.isMobile ? '100%' : 'auto'};
  height: ${props => props.isMobile ? '100%' : 'auto'};
  max-width: 100%;
  max-height: 100%;
  object-fit: ${props => props.isMobile ? 'cover' : 'contain'};
  
  @media (max-width: 767px) {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }
`;

export default function VirtualTryOnModal({
  setModalState,
  pr_images,
  psd_files,
  productInfo,
}) {
  const webCamContainerRef = useRef(null);
  const [photoWithFrameApplied, setPhotoWithFrameApplied] = useState("");
  const [imgSrc, setImgSrc] = useState(null);
  const [webcamWidth, setWebcamWidth] = useState(0);
  const [webcamHeight, setWebcamHeight] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasFrameApplied, setHasFrameApplied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [guideText, setGuideText] = useState("Position your face in the center and look straight ahead");
  const webcamRef = useRef(null);
  const modalRef = useRef(null);

  const prImageArray = pr_images.split(",");
  const psdFilesArray = psd_files.split(",");

  const phrases = [
    "Look right here – finding your perfect vision!",
    "Eye contact is the first step to finding your style.",
    "Look here! See yourself in frames that suit you best!",
    "Direct your gaze here – your perfect eyewear awaits!",
    "Got a second? Look here to find eyewear that's truly you.",
    "Look into the lens, and imagine yourself with a brand-new look!",
    "Ready to find the frames that fit you?",
    "Your new favorite look is just a glance away.",
    "Straight ahead – that's where your perfect style begins.",
    "Perfect vision starts with the right frames.",
  ];

  const applyLensOnFace = async (capturedImage) => {
    setIsProcessing(true);
    let index = 0;

    // Start cycling through phrases
    const intervalId = setInterval(() => {
      setGuideText(phrases[index]);
      index = (index + 1) % phrases.length;
    }, 2000);

    try {
      const frameBase64Response = await apiService.fetchFile(psdFilesArray[0]);
      const psdBase64Response = await apiService.fetchFile(psdFilesArray[1]);
      const frameBase64 = frameBase64Response.data;
      const psdBase64 = psdBase64Response.data;

      const postData = {
        image1: capturedImage?.split(",")[1],
        image2: frameBase64.split(",")[1],
        image3: psdBase64.split(",")[1],
      };

      const aiImageBlob = await apiService.getAIGeneratedImage(
        JSON.stringify(postData)
      );

      clearInterval(intervalId); // Stop cycling messages
      setGuideText("Looking great! Tap the camera to try again");
      setPhotoWithFrameApplied(aiImageBlob);
      setHasFrameApplied(true);
      setIsProcessing(false);
    } catch (err) {
      clearInterval(intervalId); // Stop cycling messages
      setGuideText("Position your face in the center and look straight ahead");
      setIsProcessing(false);
      setHasFrameApplied(false);
      toast.error("Something went wrong, please try again", {
        duration: 3000,
        position: "top-center",
        style: {
          background: "#333",
          color: "#fff",
        },
      });
    }
  };

  // Combined capture and apply function
  const captureAndApply = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();

    if (imageSrc) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // Use webcam dimensions for the canvas
        const videoWidth = webcamRef.current.video.videoWidth;
        const videoHeight = webcamRef.current.video.videoHeight;

        canvas.width = videoWidth;
        canvas.height = videoHeight;

        // Apply transformations: flip and style
        ctx.scale(-1, 1); // Flip horizontally
        ctx.filter = "brightness(1.0) contrast(1.0) saturate(1.0)"; // Apply filters

        // Draw the flipped image at original dimensions
        ctx.drawImage(img, -videoWidth, 0, videoWidth, videoHeight);

        // Convert the canvas back to a base64 string
        const transformedImageSrc = canvas.toDataURL("image/png");
        setImgSrc(transformedImageSrc); // Set the transformed image
        
        // Automatically apply the frame
        applyLensOnFace(transformedImageSrc);
      };
      img.src = imageSrc;
    }
  }, [webcamRef]);

  const retake = () => {
    setImgSrc(null);
    setHasFrameApplied(false);
    setGuideText("Position your face in the center and look straight ahead");
  };
  
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };
  
  const downloadImage = () => {
    if (photoWithFrameApplied) {
      const link = document.createElement('a');
      link.href = photoWithFrameApplied;
      link.download = 'spectsgenie-virtual-tryon.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success("Image downloaded successfully!", {
        duration: 2000,
        position: "top-center",
        style: {
          background: "#1F509A",
          color: "#fff",
        },
      });
    }
  };
  
  const shareImage = () => {
    if (navigator.share && photoWithFrameApplied) {
      // Create the product URL
      const productUrl = `${window.location.origin}/product/${productInfo?.id}/${productInfo?.ca_id}`;
      const productName = productInfo?.name || productInfo?.product_name || 'these frames';
      
      fetch(photoWithFrameApplied)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], 'spectsgenie-virtual-tryon.png', { type: 'image/png' });
          navigator.share({
            title: `Virtual Try-On - ${productName}`,
            text: `Check out how I look with ${productName}! 🕶️\n\nSee this product: ${productUrl}\n\nTry Virtual Try-On at SpectsGenie!`,
            files: [file]
          }).catch(err => {
            console.error('Error sharing:', err);
          });
        });
    } else {
      // Fallback for devices that don't support Web Share API
      if (photoWithFrameApplied) {
        const productUrl = `${window.location.origin}/product/${productInfo?.id}/${productInfo?.ca_id}`;
        const productName = productInfo?.name || productInfo?.product_name || 'these frames';
        
        // Copy to clipboard as fallback
        const shareText = `Check out how I look with ${productName}! 🕶️\n\nSee this product: ${productUrl}\n\nTry Virtual Try-On at SpectsGenie!`;
        
        if (navigator.clipboard) {
          navigator.clipboard.writeText(shareText).then(() => {
            toast.success("Product link copied to clipboard!", {
              duration: 3000,
              position: "top-center",
              style: {
                background: "#1F509A",
                color: "#fff",
              },
            });
          }).catch(err => {
            console.error('Failed to copy text: ', err);
            toast.error("Failed to copy link", {
              duration: 2000,
              position: "top-center",
            });
          });
        } else {
          toast.error("Sharing not supported on this device", {
            duration: 2000,
            position: "top-center",
          });
        }
      }
    }
  };

  useEffect(() => {
    if (webCamContainerRef.current) {
      setWebcamHeight(webCamContainerRef.current?.offsetHeight);
      setWebcamWidth(webCamContainerRef.current?.offsetWidth);
    }
    
    // Handle fullscreen for mobile and detect mobile devices
    const handleResize = () => {
      const mobileView = window.innerWidth <= 767;
      setIsMobile(mobileView);
      
      if (mobileView) {
        if (isFullscreen) {
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = '';
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => {
      window.removeEventListener('resize', handleResize);
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);

  return (
    <ModalOverlay>
      <ModalContainer ref={modalRef} className={isFullscreen ? 'fullscreen' : ''}>
        <CloseButton onClick={() => setModalState(false)}>
          <AiOutlineClose size={20} />
        </CloseButton>
        
        <WebcamContainer ref={webCamContainerRef}>
          {!imgSrc && !hasFrameApplied && (
            <Webcam
              height={webcamHeight}
              width={webcamWidth}
              screenshotFormat="image/png"
              ref={webcamRef}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'scaleX(-1)',
                filter: 'brightness(1.05) contrast(1.05) saturate(1.05)',
              }}
              videoConstraints={{
                width: 1280,
                height: 720,
                facingMode: "user",
              }}
            />
          )}
          
          {imgSrc && hasFrameApplied && (
            <ProcessedImageContainer>
              <ProcessedImage
                src={photoWithFrameApplied}
                alt="Frame Applied"
                isMobile={isMobile}
              />
            </ProcessedImageContainer>
          )}
          
          {isProcessing && (
            <LoadingOverlay>
              <Spinner />
              <p>{guideText}</p>
            </LoadingOverlay>
          )}
        </WebcamContainer>
        
        <ControlsBar>
          {!hasFrameApplied ? (
            <>
              <ActionButton secondary onClick={toggleFullscreen}>
                {isFullscreen ? <BsArrowsAngleContract /> : <BsArrowsFullscreen />}
              </ActionButton>
              
              <CaptureButton onClick={captureAndApply}>
                <FaCamera />
              </CaptureButton>
            </>
          ) : (
            <>
              <ActionButton secondary onClick={toggleFullscreen}>
                {isFullscreen ? <BsArrowsAngleContract /> : <BsArrowsFullscreen />}
              </ActionButton>
            
              <ActionButton secondary onClick={retake}>
                <FaRedo />
              </ActionButton>
              
              <CaptureButton onClick={retake}>
                <FaCamera />
              </CaptureButton>
              
              <ActionButton secondary onClick={downloadImage}>
                <FaDownload />
              </ActionButton>
              
              <ActionButton secondary onClick={shareImage}>
                <FaShare />
              </ActionButton>
            </>
          )}
        </ControlsBar>
      </ModalContainer>
    </ModalOverlay>
  );
}